import {
  bigint,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, updatedAt } from './columns.ts'

/**
 * Blob metadata. The bytes live in Vercel Blob; these rows are the index over
 * them, and the only thing that can answer "which files belong to this order,
 * and is anyone allowed to see them?".
 *
 * TWO stores, and the distinction is a security boundary, not a tag:
 *  - `PUBLIC`  — the marketing surface. Anyone with the URL can read it.
 *  - `PRIVATE` — client deliverables and uploads. Served only through a signed
 *    URL minted after an authorisation check; the raw URL must never be
 *    rendered into a page.
 *
 * Pblob writes these rows; P2 owns the tables so that phase cannot invent a
 * parallel one.
 */
export const assetStore = pgEnum('asset_store', ['PUBLIC', 'PRIVATE'])

/**
 * What an asset can hang off. Deliberately an enum + free-text id rather than
 * a foreign key per target: a nullable-FK-per-table design needs a migration
 * for every new attachable thing, and eleven mostly-NULL columns per row.
 * The trade-off — no referential integrity on `entityId` — is accepted and
 * paid for by cleaning up links when a parent is deleted.
 */
export const assetEntityType = pgEnum('asset_entity_type', [
  /** Anything in the catalogue — equipment, a room, a package. */
  'OFFERING',
  'OFFERING_CATEGORY',
  'CONTENT_PAGE',
  'CONTENT_BLOCK',
  'TESTIMONIAL',
  'ORDER',
  'ORDER_ITEM',
  'LEAD',
  'USER',
  'SITE_SETTING',
])

export const asset = pgTable(
  'asset',
  {
    id: primaryId('ast'),
    store: assetStore('store').notNull(),

    /** Absolute blob URL as returned by the upload. */
    url: text('url').notNull(),
    /** Key inside the store, e.g. `deliverables/ord_123/final.mp4`. */
    pathname: text('pathname').notNull(),
    contentType: text('content_type').notNull(),
    sizeBytes: bigint('size_bytes', { mode: 'number' }).notNull(),
    /** Provider checksum, for de-duplication and integrity checks. */
    checksum: text('checksum'),

    originalFileName: text('original_file_name'),
    /** Image dimensions when known; NULL for non-images. */
    width: integer('width'),
    height: integer('height'),
    /** Required for public imagery — a11y is not optional on the marketing site. */
    altText: text('alt_text'),

    uploadedByUserId: text('uploaded_by_user_id').references(() => user.id, {
      onDelete: 'set null',
    }),

    /** Soft delete: the row outlives the blob so a broken link is explainable. */
    deletedAt: timestamp('deleted_at', { withTimezone: true }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('asset_store_pathname_unique').on(t.store, t.pathname),
    index('asset_store_idx').on(t.store, t.createdAt),
    index('asset_uploader_idx').on(t.uploadedByUserId),
  ],
)

/**
 * Attaches an asset to something. `role` separates "the cover image" from "one
 * of the gallery shots" from "a deliverable the client downloads", so a query
 * can ask for exactly one of them.
 */
export const assetLink = pgTable(
  'asset_link',
  {
    id: primaryId('alnk'),
    assetId: text('asset_id')
      .notNull()
      .references(() => asset.id, { onDelete: 'cascade' }),
    entityType: assetEntityType('entity_type').notNull(),
    /** Primary key of the row in the table `entityType` names. Not an FK — see above. */
    entityId: text('entity_id').notNull(),
    /** `cover` | `gallery` | `deliverable` | `attachment` | `og` … */
    role: text('role').notNull().default('attachment'),
    sortOrder: integer('sort_order').notNull().default(0),

    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex('asset_link_unique').on(t.assetId, t.entityType, t.entityId, t.role),
    index('asset_link_entity_idx').on(t.entityType, t.entityId, t.role, t.sortOrder),
  ],
)

export type Asset = typeof asset.$inferSelect
export type NewAsset = typeof asset.$inferInsert
export type AssetLink = typeof assetLink.$inferSelect
